import { useState, useEffect } from "react";
import { useUser } from "@/client/backend/user";
import { useAcceptInvitation, useDeclineInvitation } from "@/client/backend/organization";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ReloadIcon } from "@radix-ui/react-icons";
import { useSWRConfig } from 'swr';
import { formatErrorMessage } from "@/utils/format";

export function InvitationDialog() {
  const { user } = useUser();
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const { trigger: acceptInvitation } = useAcceptInvitation();
  const { trigger: declineInvitation } = useDeclineInvitation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingOrgId, setProcessingOrgId] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<'accept' | 'decline' | null>(null);

  // Check if user has pending invitations and auto-open/close dialog
  useEffect(() => {
    if (user !== "loading") {
      if (user.invitedOrganizations && user.invitedOrganizations.length > 0) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  }, [user]);

  const handleAcceptInvitation = async (organizationId: string, organizationName: string) => {
    setIsProcessing(true);
    setProcessingOrgId(organizationId);
    setProcessingAction('accept');
    try {
      const response = await acceptInvitation({ organizationId });

      if (response?.response.status === 200) {
        toast({
          title: "Invitation accepted!",
          description: `You have successfully joined ${organizationName}.`,
        });
        
        // Refresh user data to get updated organizations
        // The useEffect will automatically close the dialog if no more invitations remain
        if (user !== "loading") {
          await mutate(`/users/${user.name}/memberships`);
          await mutate(`/users/${user.name}`);
        }
      } else {
        throw new Error("Failed to accept invitation");
      }
    } catch (error) {
      toast({
        title: "Failed to accept invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingOrgId(null);
      setProcessingAction(null);
    }
  };

  const handleDeclineInvitation = async (organizationId: string, organizationName: string) => {
    setIsProcessing(true);
    setProcessingOrgId(organizationId);
    setProcessingAction('decline');
    try {
      const response = await declineInvitation({ organizationId });

      if (response?.response.status === 200) {
        toast({
          title: "Invitation declined",
          description: `You have declined the invitation to join ${organizationName}.`,
        });
        
        // Refresh user data to get updated invitations
        // The useEffect will automatically close the dialog if no more invitations remain
        if (user !== "loading") {
          await mutate(`/users/${user.name}`);
        }
      } else {
        throw new Error("Failed to decline invitation");
      }
    } catch (error) {
      toast({
        title: "Failed to decline invitation",
        description: error instanceof Error ? formatErrorMessage(error.message) : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setProcessingOrgId(null);
      setProcessingAction(null);
    }
  };

  // Don't render if no user or no invitations
  if (user === "loading" || !user.invitedOrganizations || user.invitedOrganizations.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>You have pending invitations</DialogTitle>
          <DialogDescription>
            You've been invited to join {user.invitedOrganizations.length === 1 ? "an organization" : `${user.invitedOrganizations.length} organizations`}.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {user.invitedOrganizations.map((org) => (
            <div
              key={org.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex-1 mr-4">
                <p className="font-medium">{org.displayName}</p>
                {org.description && (
                  <p className="text-sm text-muted-foreground">{org.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeclineInvitation(org.id, org.displayName)}
                  disabled={isProcessing && processingOrgId === org.id}
                >
                  {isProcessing && processingOrgId === org.id && processingAction === 'decline' && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isProcessing && processingOrgId === org.id && processingAction === 'decline' ? "Declining..." : "Decline"}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleAcceptInvitation(org.id, org.displayName)}
                  disabled={isProcessing && processingOrgId === org.id}
                >
                  {isProcessing && processingOrgId === org.id && processingAction === 'accept' && (
                    <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isProcessing && processingOrgId === org.id && processingAction === 'accept' ? "Accepting..." : "Accept"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

